"use client";

import React, { useState, useEffect } from "react";
import {
  IconButton,
  Typography,
  Box,
  Tooltip,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Modal,
  TextField,
  Button,
} from "@mui/material";
import { Link } from "react-router-dom";
import Cookies from "js-cookie";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import LockResetIcon from "@mui/icons-material/LockReset";
import md5 from "md5";

const API_BASE_URL =
  process.env.NEXT_PUBLIC_API_BASE_URL || process.env.REACT_APP_API_URL;

const Profile = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);
  const open = Boolean(anchorEl);

  // reset password modal state
  const [resetOpen, setResetOpen] = useState(false);
  const [step, setStep] = useState(1); // 1 = verify old password, 2 = set new
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [resetError, setResetError] = useState("");
  const [resetSuccess, setResetSuccess] = useState("");
  const [resetLoading, setResetLoading] = useState(false);

  useEffect(() => {
    const userCookie = localStorage.getItem("neuro_patientportal_userName");
    if (userCookie) setUser(userCookie);
  }, []);

  const handleClick = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  const username = user;

  const handleOpenReset = () => {
    setResetError("");
    setResetSuccess("");
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setStep(1);
    setResetOpen(true);
  };

  const handleCloseReset = () => {
    if (resetLoading) return;
    setResetOpen(false);
  };

  // STEP 1: Verify old password
  const handleVerifyOldPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetSuccess("");

    if (!oldPassword) {
      setResetError("Please enter your old password.");
      return;
    }

    const token = localStorage.getItem("neuro_patientportal_auth_token");
    if (!token) {
      setResetError("You are not logged in.");
      return;
    }

    if (!API_BASE_URL) {
      setResetError("API base URL is not configured.");
      return;
    }

    try {
      setResetLoading(true);
      const hashedOldPassword = md5(oldPassword);

      const res = await fetch(
        `${API_BASE_URL}/PatientPortal/change-oldpassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ oldPassword: hashedOldPassword }),
        }
      );

      const text = await res.text();

      if (!res.ok) {
        let message = "Old Password Not Correct";
        if (text) {
          if (text.toLowerCase().includes("old password not correct")) {
            message = "Old Password Not Correct";
          } else {
            message = text;
          }
        }
        setResetError(message);
        return;
      }

      if (text && text.toLowerCase().includes("old password not correct")) {
        setResetError("Old Password Not Correct");
        return;
      }

      setResetSuccess("Old password verified. Please set your new password.");
      setStep(2);
    } catch (err) {
      setResetError(err?.message || "Failed to verify old password.");
    } finally {
      setResetLoading(false);
    }
  };

  // STEP 2: Submit new password
  const handleSubmitNewPassword = async (e) => {
    e.preventDefault();
    setResetError("");
    setResetSuccess("");

    if (!oldPassword) {
      setResetError("Please verify your old password again.");
      setStep(1);
      return;
    }

    if (!newPassword || !confirmPassword) {
      setResetError("All fields are required.");
      return;
    }
    if (newPassword === oldPassword) {
      setResetError("New password must be different from old password.");
      return;
    }
    if (newPassword !== confirmPassword) {
      setResetError("New password and confirm password must match.");
      return;
    }

    const token = localStorage.getItem("neuro_patientportal_auth_token");
    if (!token) {
      setResetError("You are not logged in.");
      return;
    }

    if (!API_BASE_URL) {
      setResetError("API base URL is not configured.");
      return;
    }

    try {
      setResetLoading(true);
      const hashedOldPassword = md5(oldPassword);
      const hashedNewPassword = md5(newPassword);

      const res = await fetch(
        `${API_BASE_URL}/PatientPortal/change-oldpassword`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            oldPassword: hashedOldPassword,
            newPassword: hashedNewPassword,
          }),
        }
      );

      if (!res.ok) {
        let message = "Failed to change password.";
        try {
          const contentType = res.headers.get("content-type") || "";
          if (contentType.includes("application/json")) {
            const data = await res.json();
            message = data?.message || message;
          } else {
            const text = await res.text();
            if (text) message = text;
          }
        } catch {
          // ignore parse error
        }
        setResetError(message);
        return;
      }

      setResetSuccess("Password updated successfully.");
      setOldPassword("");
      setNewPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        setResetOpen(false);
        setResetSuccess("");
        setStep(1);
      }, 1500);
    } catch (err) {
      setResetError(err?.message || "Failed to change password.");
    } finally {
      setResetLoading(false);
    }
  };

  // Styles for TextFields in Dark Mode (Modal)
  const inputSx = {
    ".dark-theme &": {
      "& .MuiInputLabel-root": { color: "rgba(255, 255, 255, 0.7)" },
      "& .MuiInputLabel-root.Mui-focused": { color: "#fff" },
      "& .MuiOutlinedInput-root": {
        color: "#fff",
        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.3)" },
        "&:hover fieldset": { borderColor: "rgba(255, 255, 255, 0.5)" },
        "&.Mui-focused fieldset": { borderColor: "#fff" },
      },
    },
  };

  return (
    <>
      <Tooltip title="Account">
        <IconButton
          onClick={handleClick}
          size="small"
          sx={{ p: 0, borderRadius: "5px" }}
          aria-controls={open ? "account-menu" : undefined}
          aria-haspopup="true"
          aria-expanded={open ? "true" : undefined}
        >
          <Avatar
            src="/images/patient-avatar.jpg"
            alt={username}
            sx={{
              width: { xs: 45, sm: 55 },
              height: { xs: 45, sm: 55 },
              mx: "auto",
              border: "3px solid #C2CDFF",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
            }}
          />
          <Box
            sx={{
              textAlign: "left",
              mx: 1,
              display: { xs: "none", sm: "block" },
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                color: "#111",
                lineHeight: 1,
                ".dark-theme &": { color: "#fff !important" },
              }}
            >
              {username}
            </Typography>
          </Box>
          <KeyboardArrowDownIcon
            sx={{
              fontSize: 16,
              ".dark-theme &": { color: "#fff" },
            }}
          />
        </IconButton>
      </Tooltip>

      <Menu
        anchorEl={anchorEl}
        id="account-menu"
        open={open}
        onClose={handleCloseMenu}
        onClick={handleCloseMenu}
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: "8px",
            boxShadow: "0 4px 30px rgba(0,0,0,.1)",
            mt: 1.5,
            overflow: "visible",

            // --- Dark Mode Menu Styles ---
            ".dark-theme &": {
              bgcolor: "#0e1726",
              color: "#fff",
              boxShadow: "0 4px 30px rgba(0,0,0,0.5)",
            },
            ".dark-theme & .MuiListItemIcon-root": {
              color: "#fff !important",
            },
            // NEW: force all Typography (My Profile, Reset Password, Logout) white in dark mode
            ".dark-theme & .MuiTypography-root": {
              color: "#fff !important",
            },
            // -----------------------------

            "& .MuiAvatar-root": { width: 32, height: 32, ml: -0.5, mr: 1 },
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 14,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
              ".dark-theme &": { bgcolor: "#0e1726" },
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        <MenuItem sx={{ p: "10px 20px" }}>
          <Box />
        </MenuItem>

        <Divider
          sx={{
            borderColor: "#F0F1F3",
            ".dark-theme &": { borderColor: "rgba(255,255,255,0.1)" },
          }}
        />

        {/* My Profile */}
        <MenuItem sx={{ p: "8px 20px" }}>
          <Link
            to="/dashboard/patient-profile"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit", // inherits white in dark mode
            }}
          >
            <ListItemIcon sx={{ mr: -1 }}>
              <AccountCircleIcon fontSize="small" />
            </ListItemIcon>
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              My Profile
            </Typography>
          </Link>
        </MenuItem>

        {/* Reset Password */}
        <MenuItem
          sx={{ p: "8px 20px" }}
          onClick={() => {
            handleCloseMenu();
            handleOpenReset();
          }}
        >
          <ListItemIcon sx={{ mr: -1 }}>
            <LockResetIcon fontSize="small" />
          </ListItemIcon>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Reset Password
          </Typography>
        </MenuItem>

        {/* Logout */}
        <MenuItem sx={{ p: "8px 20px" }}>
          <a
            href="/authentication/logout/"
            style={{
              display: "flex",
              alignItems: "center",
              textDecoration: "none",
              color: "inherit",
              width: "100%",
            }}
          >
            <ListItemIcon sx={{ mr: -1 }}>
              <LogoutIcon fontSize="small" color="error" />
            </ListItemIcon>
            <Typography
              variant="body2"
              sx={{ fontWeight: 500, color: "error.main" }}
            >
              Logout
            </Typography>
          </a>
        </MenuItem>
      </Menu>

      {/* Reset Password Modal */}
      <Modal open={resetOpen} onClose={handleCloseReset}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: "90%", sm: 400 },
            bgcolor: "background.paper",
            borderRadius: 2,
            boxShadow: 24,
            p: 3,

            ".dark-theme &": {
              bgcolor: "#0e1726",
              color: "#fff",
            },
          }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Reset Password
          </Typography>

          {resetError && (
            <Typography
              variant="body2"
              color="error"
              sx={{ mb: 1, whiteSpace: "pre-line" }}
            >
              {resetError}
            </Typography>
          )}
          {resetSuccess && (
            <Typography
              variant="body2"
              color="primary"
              sx={{ mb: 1, whiteSpace: "pre-line" }}
            >
              {resetSuccess}
            </Typography>
          )}

          {step === 1 && (
            <Box component="form" onSubmit={handleVerifyOldPassword}>
              <TextField
                label="Old Password"
                type="password"
                fullWidth
                margin="normal"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                required
                sx={inputSx}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 2,
                }}
              >
                <Button
                  onClick={handleCloseReset}
                  disabled={resetLoading}
                  sx={{ ".dark-theme &": { color: "#fff" } }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={resetLoading}
                >
                  {resetLoading ? "Verifying..." : "Verify"}
                </Button>
              </Box>
            </Box>
          )}

          {step === 2 && (
            <Box component="form" onSubmit={handleSubmitNewPassword}>
              <TextField
                label="New Password"
                type="password"
                fullWidth
                margin="normal"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                sx={inputSx}
              />
              <TextField
                label="Confirm Password"
                type="password"
                fullWidth
                margin="normal"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                sx={inputSx}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 1,
                  mt: 2,
                }}
              >
                <Button
                  onClick={() => {
                    setStep(1);
                    setResetSuccess("");
                  }}
                  disabled={resetLoading}
                  color="inherit"
                  sx={{ ".dark-theme &": { color: "#fff" } }}
                >
                  Back
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={resetLoading}
                >
                  {resetLoading ? "Saving..." : "Update Password"}
                </Button>
              </Box>
            </Box>
          )}
        </Box>
      </Modal>
    </>
  );
};

export default Profile;